import {
  BillingAddress,
  BillingEntityType,
  BillingProfile,
  DEFAULT_BILLING_COUNTRY,
  FiscalRegime,
} from '../../models/billing.model';
import {
  isValidCif,
  isValidTaxIdForEntityType,
  normalizeTaxId,
} from '../validation/spanish-tax-id';

function parseAddress(raw: unknown): BillingAddress | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const line1 = typeof a['line1'] === 'string' ? a['line1'].trim() : '';
  const postalCode = typeof a['postalCode'] === 'string' ? a['postalCode'].trim() : '';
  const city = typeof a['city'] === 'string' ? a['city'].trim() : '';
  const province = typeof a['province'] === 'string' ? a['province'].trim() : '';
  if (a['country'] !== DEFAULT_BILLING_COUNTRY) return null;
  if (!line1 || !postalCode || !city || !province) return null;
  return { line1, postalCode, city, province, country: DEFAULT_BILLING_COUNTRY };
}

export function parseBillingProfile(raw: unknown): BillingProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const bp = raw as Record<string, unknown>;
  const legalName = typeof bp['legalName'] === 'string' ? bp['legalName'].trim() : '';
  const taxId = typeof bp['taxId'] === 'string' ? normalizeTaxId(bp['taxId']) : '';
  const regimeRaw = bp['fiscalRegime'];
  const legacyRegime = regimeRaw === 'empresa_autonomo' || regimeRaw === 'particular'
    ? regimeRaw as FiscalRegime
    : null;
  const entityRaw = bp['entityType'];
  const explicitEntityType = entityRaw === 'persona_fisica' || entityRaw === 'persona_juridica'
    ? entityRaw as BillingEntityType
    : null;
  const entityType = explicitEntityType
    ?? (legacyRegime === 'empresa_autonomo' && isValidCif(taxId)
      ? 'persona_juridica'
      : 'persona_fisica');
  const actsAsBusiness = entityType === 'persona_juridica'
    ? true
    : typeof bp['actsAsBusiness'] === 'boolean'
      ? bp['actsAsBusiness'] as boolean
      : legacyRegime === 'empresa_autonomo';
  const fiscalRegime: FiscalRegime =
    entityType === 'persona_juridica' || actsAsBusiness ? 'empresa_autonomo' : 'particular';
  const address = parseAddress(bp['address']);
  if (!legalName || !taxId || !address) return null;
  const updatedAt = typeof bp['updatedAt'] === 'number' && Number.isFinite(bp['updatedAt'])
    ? bp['updatedAt']
    : undefined;
  return { legalName, taxId, entityType, actsAsBusiness, fiscalRegime, address, updatedAt };
}

export function isBillingProfileComplete(profile: BillingProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.legalName.trim().length < 3) return false;
  if (!isValidTaxIdForEntityType(profile.taxId, profile.entityType)) return false;
  const a = profile.address;
  if (!a.line1.trim() || !a.city.trim() || !a.province.trim()) return false;
  if (!/^\d{5}$/.test(a.postalCode.trim())) return false;
  return a.country === DEFAULT_BILLING_COUNTRY;
}

export function emptyBillingProfile(): BillingProfile {
  return {
    legalName: '',
    taxId: '',
    entityType: 'persona_fisica',
    actsAsBusiness: false,
    fiscalRegime: 'particular',
    address: {
      line1: '',
      postalCode: '',
      city: 'Madrid',
      province: 'Madrid',
      country: DEFAULT_BILLING_COUNTRY,
    },
  };
}
